import { useCallback, useEffect, useState } from "react";

import { Tooltip } from "@mantine/core";

import styles from "./HoverCopyKeyValue.module.css";

/**
 * How long the post-copy "Copied!" tooltip stays open (aligned with Mantine CopyButton).
 * @agentic connections
 */
const COPIED_HINT_MS = 2000;

/**
 * Props for compact key/value field with hover-reveal and clipboard copy on click.
 * @agentic connections
 */
export type HoverCopyKeyValueProps = {
  /** Label shown by default (e.g. plan name, EMR name + "MRN"). */
  label: string;
  /** Raw value revealed on hover and written to the clipboard (no display prefix). */
  value: string;
  /** Optional prefix only for the hover layer (e.g. "#"). */
  valueDisplayPrefix?: string;
  className?: string;
  /** Tooltip and aria hint (no raw IDs). Defaults to a generic “click to copy” message. */
  copyHint?: string;
  /**
   * Horizontal alignment of the label/value layers within the control. Defaults
   * to `"start"` (left-aligned) for list/detail surfaces. Use `"center"` when the
   * control stands alone and the label/value flip should stay visually balanced
   * regardless of which layer is wider (e.g. the task modal header insurer token).
   */
  align?: "start" | "center";
};

/**
 * Shows a compact label by default, swaps to the value on hover/focus, and copies the raw value on click.
 * @agentic connections
 */
export const HoverCopyKeyValue = ({
  label,
  value,
  valueDisplayPrefix = "",
  className,
  copyHint,
  align = "start",
}: HoverCopyKeyValueProps) => {
  const trimmed = value.trim();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }

    const id = window.setTimeout(() => {
      setCopied(false);
    }, COPIED_HINT_MS);

    return () => window.clearTimeout(id);
  }, [copied]);

  /**
   * Writes the raw value to the clipboard when supported.
   * @agentic connections
   */
  const handleCopy = useCallback(() => {
    if (!trimmed || !navigator?.clipboard?.writeText) {
      return;
    }

    void navigator.clipboard.writeText(trimmed).then(() => {
      setCopied(true);
    });
  }, [trimmed]);

  const rootClass = className ? `${styles.root} ${className}` : styles.root;

  if (!trimmed) {
    return <span className={rootClass}>{label}</span>;
  }

  const displayValue = `${valueDisplayPrefix}${trimmed}`;
  const hint = copyHint ?? "Click to copy";
  const tooltipLabel = copied ? "Copied!" : hint;

  return (
    <Tooltip
      label={tooltipLabel}
      withArrow
      {...(copied ? { opened: true } : {})}
    >
      <button
        type="button"
        className={`${rootClass} ${styles.interactive} ${
          align === "center" ? styles.center : ""
        }`}
        onClick={handleCopy}
        aria-label={hint}
      >
        <span className={styles.keyLayer}>{label}</span>
        <span className={styles.valueLayer}>{displayValue}</span>
      </button>
    </Tooltip>
  );
};
